import { create } from 'zustand';
import { i18n } from '../i18n';
import { getAbsences } from '../api/generated/absences/absences';
import { getMeAbsences } from '../api/generated/me-absences/me-absences';
import { getDocuments } from '../api/generated/documents/documents';
import type {
  FacilityFlowCoreModulesPlanningDtosAbsencesAbsenceDto,
  FacilityFlowCoreModulesPlanningDtosAbsencesAbsenceListDto,
  FacilityFlowCoreModulesPlanningDtosAbsencesCreateAbsenceRequest,
  FacilityFlowCoreModulesPlanningDtosAbsencesUpdateAbsenceRequest,
  FacilityFlowCoreModulesPlanningDtosAbsencesAbsenceSubstitutionResult,
  GetApiV1AbsencesParams,
  GetApiV1MeAbsencesParams
} from '../api/generated/models';
import { FacilityFlowCoreModulesPlanningEntitiesApprovalStatus } from '../api/generated/models';
import type {
  AbsenceDto,
  AbsenceListDto,
  CreateAbsenceRequest,
  ApprovalStatus,
  ApproveAbsenceResult,
  CertificateStatus,
  AbsenceSubstitutionResult,
  SubstitutionSkipReason,
} from '../types';
import { useToastStore } from './useToastStore';
import { chargeableDays } from '../utils/absenceDays';
import { parseAxiosError, showApiError } from '../errors';

interface UpdateAbsenceRequest {
  absenceTypeId: string;
  startDate: string;
  endDate: string;
  startTime?: string;
  endTime?: string;
  notes?: string;
  /** Verwaltung: book although the vacation/overtime account is overdrawn (after the warning). */
  acknowledgeBalanceWarning?: boolean;
}

/** Server warnings the administration may confirm; the page asks and sends again. */
export const BALANCE_WARNING_CODES = new Set([
  'planning.absences.vacationBalanceWarning',
  'planning.absences.overtimeBalanceWarning',
]);

/** A balance warning is shown as a confirmation by the page, every other error as a toast. */
function reportAbsenceError(error: unknown): void {
  if (!BALANCE_WARNING_CODES.has(parseAxiosError(error).code ?? '')) {
    showApiError(error);
  }
}

interface AbsenceFilters {
  employeeId?: string;
  startDate?: string;
  endDate?: string;
  approvalStatus?: ApprovalStatus;
}

interface AbsenceState {
  absences: AbsenceListDto[];
  /** Eigene Abwesenheiten des angemeldeten Mitarbeiters (`/me/absences`). */
  myAbsences: AbsenceListDto[];
  loading: boolean;
  error?: string;
  lastFilters?: AbsenceFilters;
  fetchAbsences: (filters?: AbsenceFilters) => Promise<void>;
  fetchMyAbsences: (filters?: Omit<AbsenceFilters, 'employeeId'>) => Promise<void>;
  getAbsenceById: (id: string) => Promise<AbsenceDto | null>;
  /** Verwaltungspfad — darf das Urlaubskonto bewusst überziehen. */
  createAbsence: (request: CreateAbsenceRequest) => Promise<AbsenceDto>;
  /** Self-Service — der Server prüft hier den Resturlaub. */
  createMyAbsence: (request: Omit<CreateAbsenceRequest, 'employeeId'>) => Promise<AbsenceDto>;
  /**
   * `asSelf` nutzt den Self-Service-Pfad (`/me/absences`): nur eigene, noch offene Anträge;
   * der Verwaltungspfad ist Verwaltung/Disposition vorbehalten (FAFD-8).
   */
  updateAbsence: (id: string, request: UpdateAbsenceRequest, asSelf?: boolean) => Promise<AbsenceDto>;
  deleteAbsence: (id: string, asSelf?: boolean) => Promise<void>;
  /** `substituteEmployeeId` übergibt die Einsätze im Zeitraum direkt an die Vertretung. */
  approveAbsence: (
    id: string,
    status: ApprovalStatus,
    rejectionReason?: string,
    substituteEmployeeId?: string,
  ) => Promise<ApproveAbsenceResult | null>;
  /** Vertretung nachträglich zuweisen — greift, was noch beim Abwesenden liegt. */
  assignSubstitute: (id: string, substituteEmployeeId: string) => Promise<AbsenceSubstitutionResult>;
  checkConflicts: (employeeId: string, startDate: string, endDate: string, excludeId?: string) => Promise<boolean>;
  /**
   * Arztbestätigung nachreichen. `asAdmin` nutzt den Verwaltungspfad — der Self-Service-Pfad
   * ist auf die eigene Abwesenheit beschränkt.
   */
  uploadCertificate: (absenceId: string, file: File, asAdmin?: boolean) => Promise<void>;
  /** Hinterlegte Bestätigung in einem neuen Tab öffnen. */
  openCertificate: (documentId: string) => Promise<void>;
  /** Id der Abwesenheit, deren Bestätigung gerade hochgeladen wird — für den Spinner an der Zeile. */
  uploadingCertificateFor: string | null;
  /**
   * Arbeitstage, die eine Abwesenheit im Zeitraum kosten würde (AZM, ohne Feiertage) — Vorschau im
   * Antrag. Ohne `employeeId` für den angemeldeten Mitarbeiter (`/me`), sonst Verwaltungspfad.
   */
  previewWorkingDays: (startDate: string, endDate: string, employeeId?: string) => Promise<number>;
}

const absenceApi = getAbsences();
const meAbsenceApi = getMeAbsences();
const { getApiV1DocumentsIdContent } = getDocuments();

const t = (key: string, options?: Record<string, unknown>) =>
  i18n.t(key, { ns: 'absences', ...options }) as string;

// why: eine Mapping-Stelle je Shape — sonst fehlt ein neues Feld (z. B. workingDays)
// in einem der vier Aufrufpfade.
const toListDto = (item: FacilityFlowCoreModulesPlanningDtosAbsencesAbsenceListDto): AbsenceListDto => ({
  absenceId: item.absenceId || '',
  employeeId: item.employeeId || '',
  employeeName: item.employeeName || null,
  absenceTypeName: item.absenceTypeName || null,
  absenceTypeColor: item.absenceTypeColor || null,
  startDate: item.startDate || '',
  endDate: item.endDate || '',
  totalDays: item.totalDays || 0,
  approvalStatus: (item.approvalStatus as ApprovalStatus) || null,
  workingDays: item.workingDays ?? null,
  rejectionReason: item.rejectionReason || null,
  certificateStatus: (item.certificateStatus as CertificateStatus) || null,
  certificateDocumentId: item.certificateDocumentId || null,
  substituteEmployeeId: item.substituteEmployeeId || null,
  substituteEmployeeName: item.substituteEmployeeName || null,
});

const toAbsenceDto = (dto: FacilityFlowCoreModulesPlanningDtosAbsencesAbsenceDto): AbsenceDto => ({
  absenceId: dto.absenceId || '',
  tenantId: dto.tenantId || '',
  employeeId: dto.employeeId || '',
  employeeName: dto.employeeName || null,
  absenceTypeId: dto.absenceTypeId || '',
  absenceTypeName: dto.absenceTypeName || null,
  absenceTypeColor: dto.absenceTypeColor || null,
  startDate: dto.startDate || '',
  endDate: dto.endDate || '',
  startTime: dto.startTime || null,
  endTime: dto.endTime || null,
  totalDays: dto.totalDays || 0,
  notes: dto.notes || null,
  approvalStatus: (dto.approvalStatus as ApprovalStatus) || null,
  approvedBy: dto.approvedBy || null,
  approvedAt: dto.approvedAt || null,
  createdAt: dto.createdAt || '',
  updatedAt: dto.updatedAt || null,
  workingDays: dto.workingDays ?? null,
  rejectionReason: dto.rejectionReason || null,
  countsAgainstVacation: dto.countsAgainstVacation ?? false,
  certificateStatus: (dto.certificateStatus as CertificateStatus) || null,
  certificateDocumentId: dto.certificateDocumentId || null,
  substituteEmployeeId: dto.substituteEmployeeId || null,
  substituteEmployeeName: dto.substituteEmployeeName || null,
});

const toSubstitution = (
  dto: FacilityFlowCoreModulesPlanningDtosAbsencesAbsenceSubstitutionResult,
): AbsenceSubstitutionResult => ({
  substituteEmployeeId: dto.substituteEmployeeId ?? '',
  substituteEmployeeName: dto.substituteEmployeeName ?? '',
  movedPlanEntryCount: dto.movedPlanEntryCount ?? 0,
  movedTaskCount: dto.movedTaskCount ?? 0,
  skipped: (dto.skipped ?? []).map((s) => ({
    date: s.date ?? '',
    taskName: s.taskName ?? '',
    reason: (s.reason as SubstitutionSkipReason) ?? 'timeOverlap',
  })),
});

const announceCreated = (created: AbsenceDto) => {
  const message = created.approvalStatus === 'Pending'
    ? t('messages.requested', { days: chargeableDays(created) })
    : t('messages.created', { days: chargeableDays(created) });

  useToastStore.getState().addToast(message, 'success');
};

// why: nach einem Schreibvorgang muss die Liste aktualisiert werden, die der Nutzer gerade
// sieht — Verwaltung (`absences`) und Self-Service (`myAbsences`) laufen über verschiedene
// Endpunkte. Es wird nur nachgeladen, was zuvor überhaupt geladen wurde.
let hasLoadedAll = false;
let hasLoadedMine = false;

export const useAbsenceStore = create<AbsenceState>((set, get) => {
  const refreshLoadedLists = async () => {
    await Promise.all([
      hasLoadedAll ? get().fetchAbsences(get().lastFilters) : Promise.resolve(),
      hasLoadedMine ? get().fetchMyAbsences() : Promise.resolve(),
    ]);
  };

  return ({
    absences: [],
    myAbsences: [],
    loading: false,
    error: undefined,
    lastFilters: undefined,
    uploadingCertificateFor: null,

    previewWorkingDays: async (startDate, endDate, employeeId) => {
      const result = employeeId
        ? await absenceApi.getApiV1AbsencesWorkingDays({ employeeId, startDate, endDate })
        : await meAbsenceApi.getApiV1MeAbsencesWorkingDays({ startDate, endDate });
      return result.workingDays ?? 0;
    },

    fetchAbsences: async (filters) => {
      set({ loading: true, error: undefined, lastFilters: filters });
      try {
        const params: GetApiV1AbsencesParams = {};

        if (filters?.employeeId) params.employeeId = filters.employeeId;
        if (filters?.startDate) params.startDate = filters.startDate;
        if (filters?.endDate) params.endDate = filters.endDate;
        if (filters?.approvalStatus) params.approvalStatus = filters.approvalStatus;

        const response = await absenceApi.getApiV1Absences(params);

        hasLoadedAll = true;
        set({ absences: (response || []).map(toListDto), loading: false });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : t('errors.fetchFailed');
        set({error: errorMessage, loading: false});
        // Kein Toast beim Fetch - Error-State wird in der UI angezeigt
      }
    },

    fetchMyAbsences: async (filters) => {
      set({ loading: true, error: undefined });
      try {
        const params: GetApiV1MeAbsencesParams = {};

        if (filters?.startDate) params.startDate = filters.startDate;
        if (filters?.endDate) params.endDate = filters.endDate;
        if (filters?.approvalStatus) params.approvalStatus = filters.approvalStatus;

        const response = await meAbsenceApi.getApiV1MeAbsences(params);

        hasLoadedMine = true;
        set({ myAbsences: (response || []).map(toListDto), loading: false });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : t('errors.fetchFailed');
        set({ error: errorMessage, loading: false });
      }
    },

    createAbsence: async (request: CreateAbsenceRequest) => {
      set({loading: true, error: undefined});
      try {
        const createRequest: FacilityFlowCoreModulesPlanningDtosAbsencesCreateAbsenceRequest = {
          employeeId: request.employeeId,
          absenceTypeId: request.absenceTypeId,
          startDate: request.startDate,
          endDate: request.endDate,
          startTime: request.startTime || null,
          endTime: request.endTime || null,
          notes: request.notes || null,
          acknowledgeBalanceWarning: request.acknowledgeBalanceWarning ?? false,
        };

        const created = toAbsenceDto(await absenceApi.postApiV1Absences(createRequest));

        await refreshLoadedLists();

        set({ loading: false });
        announceCreated(created);

        return created;
      } catch (error) {
        set({ error: parseAxiosError(error).code ?? t('errors.createFailed'), loading: false });
        reportAbsenceError(error);
        throw error;
      }
    },

    createMyAbsence: async (request) => {
      set({ loading: true, error: undefined });
      try {
        const created = toAbsenceDto(
          await meAbsenceApi.postApiV1MeAbsences({
            // Der Server überschreibt die EmployeeId mit dem angemeldeten Nutzer.
            employeeId: '00000000-0000-0000-0000-000000000000',
            absenceTypeId: request.absenceTypeId,
            startDate: request.startDate,
            endDate: request.endDate,
            startTime: request.startTime || null,
            endTime: request.endTime || null,
            notes: request.notes || null,
          }),
        );

        await refreshLoadedLists();

        set({ loading: false });
        announceCreated(created);

        return created;
      } catch (error) {
        set({ error: parseAxiosError(error).code ?? t('errors.createFailed'), loading: false });
        reportAbsenceError(error);
        throw error;
      }
    },

    getAbsenceById: async (id: string) => {
      try {
        return toAbsenceDto(await absenceApi.getApiV1AbsencesId(id));
      } catch (error) {
        console.error('Error fetching absence by ID:', error);
        return null;
      }
    },

    updateAbsence: async (id: string, request: UpdateAbsenceRequest, asSelf = false) => {
      set({loading: true, error: undefined});
      try {
        const updateRequest: FacilityFlowCoreModulesPlanningDtosAbsencesUpdateAbsenceRequest = {
          absenceTypeId: request.absenceTypeId,
          startDate: request.startDate,
          endDate: request.endDate,
          startTime: request.startTime || null,
          endTime: request.endTime || null,
          notes: request.notes || null,
          acknowledgeBalanceWarning: request.acknowledgeBalanceWarning ?? false,
        };

        const updated = toAbsenceDto(
          asSelf
            ? await meAbsenceApi.putApiV1MeAbsencesAbsenceId(id, updateRequest)
            : await absenceApi.putApiV1AbsencesId(id, updateRequest),
        );

        await refreshLoadedLists();

        set({ loading: false });

        useToastStore.getState().addToast(t('messages.updated'), 'success');

        return updated;
      } catch (error) {
        set({ error: parseAxiosError(error).code ?? t('errors.updateFailed'), loading: false });
        reportAbsenceError(error);
        throw error;
      }
    },

    deleteAbsence: async (id: string, asSelf = false) => {
      set({loading: true, error: undefined});
      try {
        if (asSelf) {
          await meAbsenceApi.deleteApiV1MeAbsencesAbsenceId(id);
        } else {
          await absenceApi.deleteApiV1AbsencesId(id);
        }

        await refreshLoadedLists();

        set({ loading: false });

        useToastStore.getState().addToast(t('messages.deleted'), 'success');
      } catch (error) {
        // why: show the translated error code (e.g. planning.absences.notPending), not the Axios text.
        set({ error: parseAxiosError(error).code ?? t('errors.deleteFailed'), loading: false });
        showApiError(error);
        throw error;
      }
    },

    approveAbsence: async (
      id: string,
      status: ApprovalStatus,
      rejectionReason?: string,
      substituteEmployeeId?: string,
    ) => {
      set({loading: true, error: undefined});
      try {
        // Map string status to backend enum
        // NUMBER_1 = Pending, NUMBER_2 = Approved, NUMBER_3 = Rejected
        const statusEnum = status === 'Approved'
          ? FacilityFlowCoreModulesPlanningEntitiesApprovalStatus.NUMBER_2
          : FacilityFlowCoreModulesPlanningEntitiesApprovalStatus.NUMBER_3;

        const approveRequest = {
          status: statusEnum,
          rejectionReason: rejectionReason || null,
          // Nur beim Genehmigen ausgewertet — der Server ignoriert sie beim Ablehnen.
          substituteEmployeeId: substituteEmployeeId || null,
        };

        const response = await absenceApi.postApiV1AbsencesIdApprove(id, approveRequest);

        await refreshLoadedLists();

        set({ loading: false });

        const message = status === 'Approved'
            ? t('messages.approved')
            : t('messages.rejected');

        useToastStore.getState().addToast(message, 'success');

        // Return result so the UI can show affected entries dialog
        if (status === 'Approved' && response?.absence) {
          const result: ApproveAbsenceResult = {
            absence: toAbsenceDto(response.absence),
            affectedPlanEntryIds: response.affectedPlanEntryIds || [],
            affectedPlanningEntryIds: response.affectedPlanningEntryIds || [],
            substitution: response.substitution ? toSubstitution(response.substitution) : null,
          };
          return result;
        }

        return null;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : t('errors.approveFailed');
        set({error: errorMessage, loading: false});
        useToastStore.getState().addToast(errorMessage, 'error');
        throw error;
      }
    },

    assignSubstitute: async (id, substituteEmployeeId) => {
      set({ loading: true, error: undefined });
      try {
        const response = await absenceApi.postApiV1AbsencesIdSubstitute(id, { substituteEmployeeId });

        await refreshLoadedLists();

        set({ loading: false });

        const substitution = toSubstitution(response);
        useToastStore.getState().addToast(
          t('messages.substituteAssigned', { name: substitution.substituteEmployeeName }),
          'success',
        );

        return substitution;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : t('errors.substituteFailed');
        set({ error: errorMessage, loading: false });
        useToastStore.getState().addToast(errorMessage, 'error');
        throw error;
      }
    },

    uploadCertificate: async (absenceId, file, asAdmin = false) => {
      set({ uploadingCertificateFor: absenceId, error: undefined });
      try {
        if (asAdmin) {
          await absenceApi.postApiV1AbsencesIdCertificate(absenceId, { file });
        } else {
          await meAbsenceApi.postApiV1MeAbsencesAbsenceIdCertificate(absenceId, { file });
        }

        // why: nachladen statt lokal patchen — der Server vergibt die Dokument-Id und leitet
        // den Bestätigungsstatus daraus ab.
        await refreshLoadedLists();

        useToastStore.getState().addToast(t('messages.certificateUploaded'), 'success');
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : t('errors.certificateUploadFailed');
        set({ error: errorMessage });
        useToastStore.getState().addToast(errorMessage, 'error');
        throw error;
      } finally {
        set({ uploadingCertificateFor: null });
      }
    },

    // why: der Blob-Endpunkt braucht das Auth-Cookie *und* den X-Tenant-Id-Header, den die
    // Axios-Instanz setzt — ein einfaches <a href> scheitert an der Tenant-Prüfung.
    openCertificate: async (documentId) => {
      try {
        const blob = await getApiV1DocumentsIdContent(documentId);
        const url = URL.createObjectURL(blob as Blob);
        window.open(url, '_blank', 'noopener');
        // Der neue Tab hält eine eigene Referenz; unsere geben wir nach dem Laden frei.
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch {
        useToastStore.getState().addToast(t('errors.certificateOpenFailed'), 'error');
      }
    },

    checkConflicts: async (employeeId: string, startDate: string, endDate: string, excludeId?: string): Promise<boolean> => {
      try {
        const response = await absenceApi.getApiV1Absences({
          employeeId,
          startDate,
          endDate,
        });

        // Filter out the current absence if editing
        const conflicts = (response || []).filter(a =>
            !excludeId || a.absenceId !== excludeId
        );

        return conflicts.length > 0;
      } catch (error) {
        console.error('Error checking conflicts:', error);
        return false;
      }
    },
  });
});
